import { motion } from "framer-motion";
import { Clock, FileText, Users } from "lucide-react";

export default function ResultCard({ result }) {
  if (!result) return null;
  const confidenceColor =
    result.confidence >= 90 ? "from-emerald-400 to-teal-400"
      : result.confidence >= 80 ? "from-sky-400 to-emerald-400"
      : "from-sky-300 to-indigo-300";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="rounded-2xl bg-white border border-slate-200 p-6"
      data-testid="result-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-400">Predicted condition</div>
          <div className="font-heading text-3xl text-slate-900 mt-1" data-testid="result-disease">{result.disease}</div>
          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 text-xs font-mono-num tracking-wider px-2.5 py-1">
            {result.severity}
          </div>
        </div>
        {result.image_preview && (
          <img src={result.image_preview} alt="scan" className="h-20 w-20 rounded-xl object-cover ring-1 ring-slate-200" />
        )}
      </div>

      <div className="mt-6">
        <div className="flex justify-between text-xs text-slate-500 mb-1.5">
          <span>Confidence</span>
          <span className="font-mono-num text-slate-900" data-testid="result-confidence">{result.confidence}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${result.confidence}%` }}
            transition={{ duration: 1.2 }}
            className={`h-full bg-gradient-to-r ${confidenceColor}`}
          />
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500 mb-2">
            <FileText className="h-3.5 w-3.5" />
            Clinical summary
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{result.description}</p>
          <div className="mt-4 text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500 mb-2">Recommendations</div>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {result.recommendations?.map((r, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-slate-400">0{i + 1}</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500 mb-2">
            <Users className="h-3.5 w-3.5" />
            Agent trace
          </div>
          <ul className="space-y-2">
            {result.agent_notes?.map((n, i) => (
              <li key={i} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-700">
                {n}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500 font-mono-num tracking-wider">
            <Clock className="h-3.5 w-3.5" />
            {result.created_at ? new Date(result.created_at).toLocaleString() : "Just now"}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
